import type { ComponentType } from "react";

export type MigrationStatusKey =
  | "new"
  | "partial"
  | "changed"
  | "failed"
  | "already-present"
  | "applied";

export const STATUS_ORDER: MigrationStatusKey[] = [
  "new", "partial", "changed", "failed", "already-present", "applied",
];

export const STATUS_LABELS: Record<MigrationStatusKey, string> = {
  new: "New",
  partial: "Partial",
  changed: "Changed",
  failed: "Failed",
  // The last run failed with an error that means the change is ALREADY in
  // place (e.g. 1060 duplicate column). Not "applied" — that would assert the
  // whole file ran when only one statement is proven — and not "failed"
  // either, which is what had operators re-running these until they collected
  // five identical errors. It needs one click to confirm, not another run.
  "already-present": "Already present",
  applied: "Applied",
};

export const STATUS_BADGE_CLASS: Record<MigrationStatusKey, string> = {
  new:     "bg-red-50 text-red-700 border border-red-200",
  partial: "bg-amber-50 text-amber-700 border border-amber-200",
  changed: "bg-purple-50 text-purple-700 border border-purple-200",
  failed:  "bg-red-100 text-red-800 border border-red-300",
  "already-present": "bg-teal-50 text-teal-700 border border-teal-200",
  applied: "bg-emerald-50 text-emerald-700 border border-emerald-200",
};

export const STATUS_DOT_BG: Record<MigrationStatusKey, string> = {
  new:     "bg-red-500",
  partial: "bg-amber-500",
  changed: "bg-purple-500",
  failed:  "bg-red-600",
  "already-present": "bg-teal-500",
  applied: "bg-emerald-500",
};

export type TargetKind = "tenant" | "registry";

export interface TargetSummary {
  id: string;            // company_id for tenants, "__registry__" for registry
  name: string;          // display name
  databaseName: string;  // actual MariaDB DB name (cma_tenant_xyz / cma_registry)
  kind: TargetKind;
}

export interface AppliedInfo {
  version: number;
  lastRun: string;       // ISO timestamp
  status: "success" | "failed" | "pending";
  errorMessage?: string;
  /** Server-side classification of the failure — see sqlErrorClassifier.js.
   *  "already_applied" means the schema change is present despite the error. */
  errorKind?:
    | "already_applied"
    | "data_conflict"
    | "missing_object"
    | "syntax"
    | "permission"
    | "unknown";
  errorReason?: string;
  errorDetail?: string;
  errorCode?: number | null;
}

export interface MigrationRowData {
  id: number;            // registry_migrations.id
  folderName: string;    // e.g. "migrate-2026-6-7-operations-map-v2"
  fileName: string;      // e.g. "01-add-operations-map-v2-feature-flag.sql"
  folderPath: string;    // full "folder/filename" path used as archive key (same as FolderMigration.folderPath)
  version: number;       // current version on disk
  scope: TargetKind;
  scopeInferred: boolean;
  contentChanged: boolean;    // disk hash differs from registered hash for prior version
  hash: string;
  appliedTo: Record<string, AppliedInfo>;   // keyed by databaseName
  sqlPreview?: string;        // first ~80 chars of significant SQL
  everApplied: boolean;       // any successful run anywhere
  archived: boolean;
  archivedAt: string | null;
  /** e.g. "older than 30 days" (automatic) or the reason given when archived by hand. */
  archivedReason?: string | null;
}

export interface BulkRunEvent {
  type: "start" | "begin" | "success" | "skipped" | "failure" | "aborted" | "complete";
  runId?: string;
  migrationId?: number;
  target?: string;
  totalOperations?: number;
  error?: string;
  reason?: string;
}

// Re-export for consumer use
export type { ComponentType };
